/**
 * HubIconButton — the square icon button (Design System 2.0, D-017 B2; the
 * published HubIconButton page; first drawn as Portal V2 kit.jsx `IcBtn`).
 *
 * A real `<button type="button">`: 40px square beside buttons and fields
 * (the default — it was 34), 32 in rows, dense toolbars and bars. A 1px
 * `--rule` hairline on `--paper`, the icon in `--ink-soft` (16, or 14 at 32 and
 * below), 4px corners. Hover lifts the ground to `--paper-2` and the icon to
 * `--ink`; a press scales to .975 — both carried by the component itself so it
 * behaves the same inside or outside a `V2Root`. `on` marks a toggle that is
 * on: an indigo hairline on `--indigo-soft`, the icon indigo. Every icon button
 * names its action in `title` (its tooltip and accessible name). Icons come
 * from the one line set (`components/v2/icons`).
 */
import React from 'react';
import { Box } from '@mui/material';
import { PIcon } from '../v2/icons';
import type { V2IconName } from '../v2/icons';

export type HubIconButtonProps = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'color'> & {
  /** The icon to draw, from the one line set. */
  name?: V2IconName;
  /** A path in the same line set (24 grid) when the glyph is not named yet; `name` wins. */
  d?: string;
  /** Tooltip / accessible name. */
  title?: string;
  /** A toggle that is on: indigo hairline on indigo-soft. */
  on?: boolean;
  /** Square size in px (default 40; 32 in rows and toolbars). */
  size?: number;
  /** Icon size in px (default 16, or 14 at 32 and below). */
  iconSize?: number;
  /** Icon colour (default `--indigo` when on, else `--ink-soft`, `--ink` on hover). */
  color?: string;
};

/* The 2.0 transitions + press, and the hover that lifts the ground (and the
   icon to ink, unless the caller gave it a colour or it is on). */
const TRANSITION = 'transform var(--dur-state, 120ms) var(--ease-out), background var(--dur-state, 120ms) var(--ease-out), border-color var(--dur-state, 120ms) var(--ease-out), color var(--dur-state, 120ms) var(--ease-out)';
const SX = {
  transition: TRANSITION,
  '&:hover': { background: 'var(--paper-2) !important', color: 'var(--ink) !important' },
  '&:active:not(:disabled)': { transform: 'scale(.975)' },
} as const;
const SX_KEEP_COLOUR = {
  transition: TRANSITION,
  '&:hover': { background: 'var(--paper-2) !important' },
  '&:active:not(:disabled)': { transform: 'scale(.975)' },
} as const;

export function HubIconButton({ name, d, title, on, size = 40, iconSize, color, style, ...rest }: HubIconButtonProps): React.ReactElement {
  const glyph = iconSize ?? (size <= 32 ? 14 : 16);
  return (
    <Box component="button" type="button" title={title} aria-label={rest['aria-label'] ?? title} className="v2-hv" sx={color || on ? SX_KEEP_COLOUR : SX} style={{ width: size, height: size, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', border: `1px solid ${on ? 'var(--indigo)' : 'var(--rule)'}`, background: on ? 'var(--indigo-soft)' : 'var(--paper)', color: color || (on ? 'var(--indigo)' : 'var(--ink-soft)'), borderRadius: 4, cursor: 'pointer', padding: 0, flex: 'none', ...style }} {...rest}>
      <PIcon name={name} d={name ? undefined : d} size={glyph} color="currentColor" />
    </Box>
  );
}

export default HubIconButton;
